/**
 * Shared constants for the Magnus Create surface (`/create`).
 *
 * Client-safe: no Node-flavored imports. The web app imports these to build
 * the design preview URL and the create-surface model default, so the
 * capability prompt, the version snapshots, and the frontend iframe can never
 * drift on the canonical filename.
 */
import { defaultModel, type ModelId } from "../../models/config";

/**
 * The canonical entry document a create chat iterates on. Lives in the chat's
 * linked Create PROJECT files (the read-write `/home/user/current-project`
 * sandbox mount over the `create-projects` blob container), so every chat
 * linked to the project edits the same design and rclone write-back persists
 * edits without a publish step.
 *
 * `index.html` because a creation is a static site bundle, and that is the
 * name that makes one work unattended: a directory index resolves at `/` on
 * any host, in a downloaded zip, and behind the artifact publisher (whose
 * `entryFile` column defaults to it). It also matches how the rest of the
 * platform already resolves bundle entries — `artifact-import.ts`, the
 * web-artifacts-builder skill — so no boundary has to translate the name.
 */
export const CREATE_ENTRY_FILENAME = "index.html";

/**
 * What the entry document was called before `index.html`. Projects created
 * then still hold `design.html`, so READ paths resolve it as a fallback.
 * Nothing writes it: a legacy project keeps its own name (writes go to
 * whichever entry it already has) rather than being rewritten underneath its
 * owner.
 */
export const LEGACY_CREATE_ENTRY_FILENAME = "design.html";

/** Entry-document names in resolution order — canonical first. */
export const CREATE_ENTRY_FILENAMES = [
  CREATE_ENTRY_FILENAME,
  LEGACY_CREATE_ENTRY_FILENAME,
] as const;

export { CREATE_PROJECT_NAME_MAX } from "@magnus/contract/values";

/**
 * What a creation is called before anything has named it.
 *
 * A placeholder, and treated as one everywhere: it is the only name the first
 * generated chat title is allowed to replace (conditionally, so a manual
 * rename mid-turn still wins), and downloads skip past it rather than shipping
 * a file called "untitled creation". Exported so the writer, the conditional
 * write, the "still needs a title" check, and the download namer are all
 * asking about the same string.
 */
export const DEFAULT_CREATE_PROJECT_NAME = "Untitled creation";

/**
 * The STRUCTURAL kind of a creation — three values, and they carry weight
 * rather than describing taste:
 *
 * - `slides`: fixed-landscape pages consumed as a presentation (presenter
 *   chrome, transitions, thumbnail rail).
 * - `document`: paginated pages at a chosen paper size, consumed one page at
 *   a time and printed/exported to match.
 * - `freeform`: everything else — a page, app, microsite, animation, game.
 *   No runtime wrapper beyond the fit/interception fixes every design gets.
 *
 * This is the value the present runtime, the exports, and the prompt's
 * format contract all read. Semantic flavors (wireframe, mockup, diagram,
 * email, …) used to live in this same enum, which made "is it a wireframe"
 * look like the same kind of question as "is it paginated"; they are now
 * request-inferred guidance inside `freeform`.
 */
export type CreateProjectKind = "slides" | "document" | "freeform";

/** Every structural kind, in picker order. */
export const CREATE_PROJECT_KINDS: CreateProjectKind[] = [
  "slides",
  "document",
  "freeform",
];

/**
 * Type ids from the eight-way semantic list this triad replaced. They still
 * sit in `create_project.projectType` (plain text) and in old chats' stored
 * composer selections, and every one of them was structurally freeform.
 */
const LEGACY_FREEFORM_TYPE_IDS = new Set([
  "microsite",
  "animation",
  "wireframe",
  "mockup",
  "diagram",
  "email",
  "prototype",
  "app",
]);

/**
 * Narrow a stored `projectType` to a structural kind. Legacy semantic ids
 * resolve to `freeform` rather than to nothing: they describe real designs
 * that must keep presenting the way they always did, and freeform is what
 * they always structurally were. Undefined means "no type declared".
 */
export function asProjectKind(
  value: string | null | undefined
): CreateProjectKind | undefined {
  if (!value) {
    return;
  }
  if ((CREATE_PROJECT_KINDS as string[]).includes(value)) {
    return value as CreateProjectKind;
  }
  return LEGACY_FREEFORM_TYPE_IDS.has(value) ? "freeform" : undefined;
}

/** Slide canvas, in CSS px. The one geometry `slides` does not get to pick. */
export const SLIDE_WIDTH_PX = 1280;
export const SLIDE_HEIGHT_PX = 720;

export type CreatePageSize = {
  id: "letter" | "legal" | "tabloid" | "a4" | "a3";
  label: string;
  /** Portrait width in CSS px at 96dpi. */
  widthPx: number;
  /** Portrait height in CSS px at 96dpi. */
  heightPx: number;
  /**
   * The CSS `@page { size: … }` page NAME, when the spec has one — a named
   * size lets the print engine pick the real paper (and takes the
   * `landscape` keyword). Null where there is no name (Tabloid), and the
   * `@page` value falls back to explicit dimensions.
   */
  cssPageName: string | null;
  /** Human dimensions for pickers and prompt text ("8.5 × 11 in"). */
  dimensions: string;
};

/**
 * The paper sizes a `document` creation can be built at. CSS px at 96dpi is
 * the unit because that is what the design is laid out in, what the present
 * runtime measures, and what the export viewport is sized to — inches only
 * appear in `@page` and in labels.
 */
export const PAGE_SIZES: CreatePageSize[] = [
  {
    id: "letter",
    label: "US Letter",
    widthPx: 816,
    heightPx: 1056,
    cssPageName: "letter",
    dimensions: "8.5 × 11 in",
  },
  {
    id: "legal",
    label: "US Legal",
    widthPx: 816,
    heightPx: 1344,
    cssPageName: "legal",
    dimensions: "8.5 × 14 in",
  },
  {
    id: "tabloid",
    label: "Tabloid",
    widthPx: 1056,
    heightPx: 1632,
    cssPageName: null,
    dimensions: "11 × 17 in",
  },
  {
    id: "a4",
    label: "A4",
    widthPx: 794,
    heightPx: 1123,
    cssPageName: "A4",
    dimensions: "210 × 297 mm",
  },
  {
    id: "a3",
    label: "A3",
    widthPx: 1123,
    heightPx: 1587,
    cssPageName: "A3",
    dimensions: "297 × 420 mm",
  },
];

export type CreatePageSizeId = CreatePageSize["id"];
export type CreatePageOrientation = "portrait" | "landscape";

/** The size a `document` gets when the user never picked one. */
export const DEFAULT_PAGE_SIZE_ID: CreatePageSizeId = "letter";
export const DEFAULT_PAGE_ORIENTATION: CreatePageOrientation = "portrait";

/** Narrow a stored page-size id; undefined when unrecognized. */
export function asPageSizeId(
  value: string | null | undefined
): CreatePageSizeId | undefined {
  return PAGE_SIZES.find((s) => s.id === value)?.id;
}

/** Narrow a stored orientation; undefined when unrecognized. */
export function asPageOrientation(
  value: string | null | undefined
): CreatePageOrientation | undefined {
  return value === "portrait" || value === "landscape" ? value : undefined;
}

/**
 * Resolved page geometry: CSS-px dimensions with orientation applied, plus
 * the `@page { size: … }` value to match. One function so the prompt's dims
 * table, the runtime's fit, the export viewport, and the picker's label can
 * never disagree about what "tabloid landscape" measures.
 */
export function resolvePageSetup(params?: {
  pageSize?: string | null;
  pageOrientation?: string | null;
}): {
  size: CreatePageSize;
  orientation: CreatePageOrientation;
  widthPx: number;
  heightPx: number;
  /** Full `@page` size value, orientation included. */
  cssSize: string;
} {
  const size =
    PAGE_SIZES.find((s) => s.id === asPageSizeId(params?.pageSize)) ??
    PAGE_SIZES.find((s) => s.id === DEFAULT_PAGE_SIZE_ID);
  if (!size) {
    throw new Error("PAGE_SIZES is missing its default entry");
  }
  const orientation =
    asPageOrientation(params?.pageOrientation) ?? DEFAULT_PAGE_ORIENTATION;
  const landscape = orientation === "landscape";
  const widthPx = landscape ? size.heightPx : size.widthPx;
  const heightPx = landscape ? size.widthPx : size.heightPx;
  return {
    size,
    orientation,
    widthPx,
    heightPx,
    cssSize: size.cssPageName
      ? `${size.cssPageName} ${orientation}`
      : `${widthPx}px ${heightPx}px`,
  };
}

/** Absolute sandbox path of the mounted project-files directory. */
export const CREATE_PROJECT_MOUNT_PATH = "/home/user/current-project";

/** Absolute sandbox path of the canonical entry document. */
export const CREATE_ENTRY_PATH = `${CREATE_PROJECT_MOUNT_PATH}/${CREATE_ENTRY_FILENAME}`;

/** Absolute sandbox paths of every entry candidate, canonical first. */
export const CREATE_ENTRY_PATHS = CREATE_ENTRY_FILENAMES.map(
  (name) => `${CREATE_PROJECT_MOUNT_PATH}/${name}`
);

/**
 * The native PowerPoint deck a `slides` creation is authored in. The HTML
 * beside it is a rendered viewer of this file, never the source of truth.
 */
export const POWERPOINT_SOURCE_FILENAME = "presentation.pptx";

/** Absolute sandbox path of the native PowerPoint source. */
export const POWERPOINT_SOURCE_PATH = `${CREATE_PROJECT_MOUNT_PATH}/${POWERPOINT_SOURCE_FILENAME}`;

/**
 * The marker the PowerPoint renderer stamps into the HTML it produces, and
 * the one thing that tells every later reader "this page is a picture of a
 * deck, not a page someone wrote".
 *
 * It has four independent consumers — the renderer that writes it, the save
 * path that refuses to overwrite a rendered deck with edited HTML, the
 * preview's layout script, and the present runtime's ownership check — in
 * three different packages. Typed out by hand in each, one of them changing
 * the tag would silently turn the other three into no-ops that fail open.
 *
 * Both spellings are here because two of those consumers run as injected
 * browser scripts, where a CSS selector is the only way to ask, while the
 * server-side ones only ever hold the document as a string.
 */
export const NATIVE_POWERPOINT_META_TAG =
  '<meta name="magnus-source" content="powerpoint">';
export const NATIVE_POWERPOINT_META_SELECTOR =
  'meta[name="magnus-source"][content="powerpoint"]';

/**
 * Whether this HTML was rendered from a native PowerPoint source.
 *
 * Matches the attribute pair rather than the whole tag so that HTML which
 * has been through a re-serializer still reads as a deck. Every caller
 * treats a match as the cautious answer — refuse the overwrite, disable the
 * editor — so the looser test is the safer one.
 */
export function isNativePowerPointHtml(html: string): boolean {
  return html.includes('name="magnus-source" content="powerpoint"');
}

/**
 * Create chats default to Sonnet 5.5: the surface is a tight loop of many
 * small edits (element tweaks, comment batches, exports), and Sonnet's
 * latency keeps the preview feeling live while its layout craft holds up.
 * A default, not a lock — an explicit user model choice always wins (see
 * the chat route's model resolution).
 */
export const CREATE_DEFAULT_MODEL_ID: ModelId = "claude-sonnet-5.5";

/**
 * The concrete model an unrouted "auto" selection runs on for a surface.
 * Create never routes and never inherits the app-wide `defaultModel`:
 * picking Auto on /create always lands on `CREATE_DEFAULT_MODEL_ID`.
 */
export function autoModelForSurface(appSurface: string | undefined): ModelId {
  return appSurface === "create" ? CREATE_DEFAULT_MODEL_ID : defaultModel;
}

/**
 * Model for mechanical inline edits (the preview's type-on-it text
 * replacements): the instruction is an exact find-and-replace needing zero
 * design judgment, so a fast model keeps the save latency low. Free-form
 * prompt edits stay on the chat's selected model.
 */
export const CREATE_FAST_EDIT_MODEL_ID: ModelId = "claude-sonnet-5.5";

/**
 * Export basename when the design declares no usable <title>. Deliberately
 * NOT derived from the entry filename: "index.pdf" says nothing about what
 * the user just downloaded.
 */
const EXPORT_BASENAME_FALLBACK = "design";
const EXPORT_BASENAME_MAX = 64;
const TITLE_TAG_RE = /<title[^>]*>([^<]*)<\/title>/i;

/**
 * Kebab-case basename for exported files (PDF/PPTX/PNG), derived from the
 * design's own `<title>` — the entry file is always the same canonical name,
 * so without this every export downloads as the meaningless "design.pdf".
 */
export function designExportBasename(html: string): string {
  const title = TITLE_TAG_RE.exec(html)?.[1]?.trim();
  if (!title) {
    return EXPORT_BASENAME_FALLBACK;
  }
  const slug = title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, EXPORT_BASENAME_MAX)
    .replace(/-+$/g, "");
  return slug || EXPORT_BASENAME_FALLBACK;
}
